import { useCallback, useEffect, useState } from "react";
import notificationManager from "services/Notification/notificationManager";
import { NotificationBadgeResponse } from "services/Notification/notificationInterface";
import { NOTIFICATIONS_REFRESH_EVENT } from "pages/firebase/NotificationProvider";

/* ────────────────────────────────────────────────────────────
   Notification badge summary for the signed-in user.

   One place fetches GET /api/notifications/badge and shares the result with the
   sidebar (tab red-dots) and the notification bell/panel.

   No polling: the badge is fetched exactly ONCE when the user becomes active
   (login + notifications enabled). After that, Firebase Cloud Messaging drives
   every update — a foreground push (onMessage) or a background push (service
   worker) dispatches `NOTIFICATIONS_REFRESH_EVENT`, which triggers a single
   refetch here. markAllRead also reconciles once after the user acts.
──────────────────────────────────────────────────────────── */

interface UseNotificationBadgeOptions {
  /** Skipped entirely when false (e.g. inactive / logged-out users). */
  enabled: boolean;
}

export const useNotificationBadge = ({ enabled }: UseNotificationBadgeOptions) => {
  const [data, setData] = useState<NotificationBadgeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchBadge = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await notificationManager.getBadge();
      setData(res?.data ?? null);
      setError(false);
    } catch (err) {
      console.error("Failed to fetch notification badge:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Mark every notification as read, then re-sync from the server. Optimistically
  // clears the badge so the UI updates instantly; a re-fetch reconciles the truth.
  const markAllRead = useCallback(async () => {
    try {
      await notificationManager.readAll();
      setData({ totalCount: 0, categories: {} });
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    } finally {
      await fetchBadge();
    }
  }, [fetchBadge]);

  // One-time fetch when the user becomes active (login + notifications enabled).
  // No interval/focus polling — FCM pushes keep the count fresh after this.
  useEffect(() => {
    if (!enabled) {
      setData(null);
      return;
    }
    void fetchBadge();
  }, [enabled, fetchBadge]);

  // FCM-driven refresh: a foreground push (onMessage) or a background push
  // (service worker → postMessage → NotificationProvider) dispatches this event;
  // each one triggers a single badge refetch. This replaces the old poll.
  useEffect(() => {
    if (!enabled) return;
    const onPush = () => void fetchBadge();
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onPush);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onPush);
  }, [enabled, fetchBadge]);

  return { data, loading, error, refresh: fetchBadge, markAllRead };
};

export default useNotificationBadge;
